import { useEffect, useState } from 'react';

import {
  parseQueryString,
  parseAmplifyConfig,
  useAuth,
  cloneSearchParams,
  containsEmailDomain,
} from '@warden-ui/common';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CupixWorksApi } from "@warden-ui/api";
import { CPDialog, CPPageBody } from '@warden-ui/common-components';
import { CardContentTeamChoose, TeamChooseInfo } from '@warden-ui/card-components';
import { useTranslation } from 'react-i18next';

const defaultQueryString: { [key: string]: string } = {
  config: '',
  region: '',
  email: '',
  clear: 'false',
};

// NOTE: this page should be entered after sign in success. (access token required)
export default function TeamChoose() {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const queryString = parseQueryString(searchParams, defaultQueryString);
  const [t] = useTranslation();
  const navigate = useNavigate();
  const amplifyConfig = parseAmplifyConfig(queryString);
  auth.configureAmplify(amplifyConfig);

  const [teamInfo, setTeamInfo] = useState<TeamChooseInfo[]>([]);
  const [chosenTeam, setChosenTeam] = useState<TeamChooseInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let redirected = false;

    const cupixWorksApiEndpoint =
      amplifyConfig[amplifyConfig.region]?.cupixworksApiEndpoint;
    if (!cupixWorksApiEndpoint) {
      setErrorMessage(t('errors.config_cupixworks_api_endpoint'));
      return;
    }

    auth
      .fetchAuthSession(amplifyConfig)
      .then((authSession) => {
        const accessToken = authSession.tokens?.accessToken.toString();
        if (!accessToken) {
          throw new Error('Access token is not found.');
        }

        return new CupixWorksApi(cupixWorksApiEndpoint).getTeams(
          accessToken,
        );
      })
      .then((teams) => {
        return setTeamInfo([
          ...teams.map(team => {
            return {
              id: team.id,
              name: team.name,
              domain: team.domain,
              logo: team.logo_urls?.thumb,
              signInMethod: team.sign_in_methods,
              ssoForcedEmailDomains: team.sso_forced_email_domains,
              isAdmin: team.user.email === queryString.email,
              hasJoined: team.signed_up,
              useSelfSignup: team.use_self_signup,
            };
          })
        ]);
      })
      .catch((error) => {
        console.error(error.message);
        setErrorMessage(t('errors.config_cupixworks_api_endpoint'));
      });

      return () => {
        redirected = true;
      }
  }, []);

  const redirectTo = (path: string, teamDomain: string | undefined) => {
    const params = cloneSearchParams(searchParams);
    params.set("clear", "false");
    params.set("team_domain", teamDomain ?? '');
    params.delete('skip_team');

    navigate(`/${path}?${params.toString()}`);
  }

  const onClickTeamChoose = (team: TeamChooseInfo) => {
    // NOTE: if the team set sso_forced_email_domains, and the user email is in the list
    if (containsEmailDomain(queryString.email, team.ssoForcedEmailDomains)) {
      setChosenTeam(team);
      return;
    }

    // NOTE: if the team only support SSO sign in (not email sign in)
    if (!team.signInMethod.includes('email')) {
      setChosenTeam(team);
      return;
    }

    redirectTo('redirect', team.domain);
  };

  const onClickResignin = async (team: TeamChooseInfo | null) => {
    await auth.signOut(amplifyConfig);

    redirectTo('', team?.domain)
    setChosenTeam(null);
  }

  if (teamInfo.length === 0) {
    // TODO: show loading or error page
    return null;
  }

  if (teamInfo.length === 1) {
    const confirmRequired = containsEmailDomain(queryString.email, teamInfo[0].ssoForcedEmailDomains);
    if (!confirmRequired) {
      redirectTo('redirect', teamInfo[0].domain);
      return null;
    }
  }

  return (
    <CPPageBody>
      <CardContentTeamChoose
        teamInfo={teamInfo}
        onClickTeamChoose={onClickTeamChoose}
        errorMessage={errorMessage}
      />

      <CPDialog
        title={t('team_choose.dialog.title')}
        content={t('team_choose.dialog.content')}
        open={!!chosenTeam}
        onClose={() => setChosenTeam(null)}
        actions={[
          {
            text: t('team_choose.dialog.action'),
            onClick: () => onClickResignin(chosenTeam),
          },
        ]}
      />
    </CPPageBody>
  );
}
